import React from 'react';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { IoAdd } from 'react-icons/io5';
import { getDatalists } from 'features/datalist';
import './MyFolder.scss';
import useMyFolderVM from './useMyFolderVM';
import { Loader } from 'components/Loader/Loader';
import { UserPageHeader } from 'components/UserPageHeader/UserPageHeader';
import { UserCard } from 'components/UserCard/UserCard';
import { EditableCommentField } from 'components/EditableCommentField/EditableCommentField';
import { StakeholderCard } from 'components/StakeholderCard/StakeholderCard';
import { ScrollToTopButton } from 'components/ScrollToTopButton/ScrollToTopButton';
import { Button } from 'components/Button/Button';
import { Modal } from 'components/Modal/Modal';
import charterPdf from 'assets/files/charte-cooperation.pdf';
import myFolderIcon from 'assets/images/icons/users/my-folder.svg';
import personalInfoIcon from 'assets/images/icons/users/personal-info.svg';
import targetIcon from 'assets/images/icons/users/target.svg';
import familyIcon from 'assets/images/icons/users/family.svg';
import contactIcon from 'assets/images/icons/users/contact.svg';
import downloadIcon from 'assets/images/icons/users/download.svg';
import plusIcon from 'assets/images/icons/users/plus.svg';
import checkIcon from 'assets/images/icons/users/check.svg';

export const MyFolder: React.FC = () => {
    const {
        me,
        isLoading,
        isAddStakeholderOpen,
        setIsAddStakeholderOpen,
        handleSavePersonalComment,
        handleSaveStakeholdersComment,
        handleSaveInitialRequestComment,
        handleAddStakeholder
    } = useMyFolderVM();
    const { datalists } = useSelector(getDatalists);
    const navigate = useNavigate();

    if (isLoading || !me) {
        return (
            <main id="my-folder">
                <Loader />
            </main>
        );
    }

    const person = me as any;
    const project = person?.projects?.[0];
    const stakeholders = person?.stakeholders ?? [];
    const responsible = person?.life_facilitator;
    const facilitator = person?.responsible_person;

    return (
        <main id="my-folder">
            <UserPageHeader icon={<img src={myFolderIcon} alt="" />} title="Mon dossier" />

            {person?.is_outgoing && (
                <section className="my-folder__outgoing">
                    <h2 className="my-folder__outgoing-title">Vous êtes sorti(e) du dispositif</h2>
                    <p>Votre accompagnement par le dispositif est terminé.</p>
                    {person?.outgoing_reason && (
                        <p className="my-folder__outgoing-reason">
                            <strong>Motif de sortie :</strong> {person.outgoing_reason}
                        </p>
                    )}
                </section>
            )}

            <UserCard icon={<img src={personalInfoIcon} alt="" />} title="Mes informations personnelles">
                <div className="my-folder__split">
                    <dl className="my-folder__info-list">
                        <div>
                            <dt>Prénom :</dt>
                            <dd>{me.firstname}</dd>
                        </div>
                        <div>
                            <dt>Nom :</dt>
                            <dd>{me.lastname}</dd>
                        </div>
                        <div>
                            <dt>Adresse E-mail :</dt>
                            <dd>{me.email}</dd>
                        </div>
                        <div>
                            <dt>Numéro de téléphone :</dt>
                            <dd>{me.phone}</dd>
                        </div>
                        <div>
                            <dt>Date de naissance :</dt>
                            <dd>{me.born_date ? moment(me.born_date).format('DD/MM/YYYY') : ''}</dd>
                        </div>
                        <div>
                            <dt>Adresse :</dt>
                            <dd>{person?.address?.formatted ?? ''}</dd>
                        </div>
                    </dl>
                    <div>
                        <p className="my-folder__charter-status">
                            <span className="my-folder__check" aria-hidden="true">
                                <img src={checkIcon} alt="" />
                            </span>
                            J'ai signé la charte de coopération.
                        </p>
                        <a href={charterPdf} download className="my-folder__charter-download">
                            <img src={downloadIcon} alt="" aria-hidden="true" />
                            <span>Télécharger le document de charte de coopération</span>
                        </a>
                        <EditableCommentField
                            label={`Commentaire réservé à ${me.firstname} :`}
                            value={person?.user?.personal_comment}
                            onSave={handleSavePersonalComment}
                        />
                    </div>
                </div>
            </UserCard>

            <UserCard icon={<img src={targetIcon} alt="" />} title="Demande initiale du projet">
                <p className="user-card__ask">
                    <strong>Intitulé de la demande :</strong>{' '}
                    {sanitizeHtml(project?.initial_request ?? '', { allowedTags: [], allowedAttributes: {} })}
                </p>
                <EditableCommentField
                    label={`Commentaire écrit par ${me.firstname} :`}
                    value={(project as any)?.user_initial_request_comment}
                    onSave={handleSaveInitialRequestComment}
                />
            </UserCard>

            <UserCard icon={<img src={familyIcon} alt="" />} title="Proches">
                <div className="my-folder__stakeholders-grid">
                    {stakeholders.map((s: any, i: number) => (
                        <StakeholderCard
                            key={s.id ?? i}
                            number={i + 1}
                            firstname={s.firstname}
                            lastname={s.lastname}
                            email={s.email}
                            phone={s.phone}
                            status={s.status ? datalists?.stakeholder_status?.[s.status] : undefined}
                        />
                    ))}
                </div>
                <Button
                    label="Ajouter un proche"
                    variant="dark"
                    type="button"
                    icon={<img src={plusIcon} alt="" />}
                    iconBefore
                    onClick={() => setIsAddStakeholderOpen(true)}
                />
                <EditableCommentField
                    label={`Commentaire écrit par ${me.firstname} :`}
                    value={person?.user?.stakeholders_comment}
                    onSave={handleSaveStakeholdersComment}
                />
            </UserCard>

            <UserCard icon={<img src={contactIcon} alt="" />} title="Coordonnées des professionnels associés">
                <div className="my-folder__split">
                    <div>
                        <h3 className="my-folder__pro-badge">Mon faciliteur de vie</h3>
                        {facilitator ? (
                            <dl className="my-folder__info-list">
                                <div>
                                    <dt>Prénom :</dt>
                                    <dd>{facilitator.firstname}</dd>
                                </div>
                                <div>
                                    <dt>Nom :</dt>
                                    <dd>{facilitator.lastname}</dd>
                                </div>
                                <div>
                                    <dt>Adresse E-mail :</dt>
                                    <dd>{facilitator.email}</dd>
                                </div>
                                <div>
                                    <dt>Numéro de téléphone :</dt>
                                    <dd>{facilitator.phone}</dd>
                                </div>
                            </dl>
                        ) : (
                            <p>Pas de faciliteur de vie renseigné</p>
                        )}
                    </div>
                    <div>
                        <h3 className="my-folder__pro-badge">Responsable</h3>
                        {responsible ? (
                            <dl className="my-folder__info-list">
                                <div>
                                    <dt>Prénom :</dt>
                                    <dd>{responsible.firstname}</dd>
                                </div>
                                <div>
                                    <dt>Nom :</dt>
                                    <dd>{responsible.lastname}</dd>
                                </div>
                                <div>
                                    <dt>Adresse E-mail :</dt>
                                    <dd>{responsible.email}</dd>
                                </div>
                                <div>
                                    <dt>Numéro de téléphone :</dt>
                                    <dd>{responsible.phone}</dd>
                                </div>
                            </dl>
                        ) : (
                            <p>Pas de responsable renseigné</p>
                        )}
                    </div>
                </div>
            </UserCard>

            {!person?.is_outgoing && (
                <section className="my-folder__exit-request">
                    <div className="my-folder__exit-request-text">
                        <h2>Souhaitez-vous sortir du dispositif ?</h2>
                        <p>
                            Vous pouvez demander une sortie du dispositif. Votre faciliteur de vie en sera informé et vous proposera un
                            rendez-vous pour en discuter.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="my-folder__exit-request-btn"
                        onClick={() => navigate('/app/user/new-appointment', { state: { startExit: true } })}
                    >
                        <IoAdd size={26} aria-hidden="true" />
                        <span>Demander une sortie du dispositif</span>
                    </button>
                </section>
            )}

            <ScrollToTopButton />

            <Modal
                open={isAddStakeholderOpen}
                setClose={() => setIsAddStakeholderOpen(false)}
                title="Ajouter un proche"
                yesText="Ajouter"
                noText="Annuler"
                onClose={() => setIsAddStakeholderOpen(false)}
                onValidate={() => {
                    document.getElementById('addStakeholderForm')?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                }}
                formId="addStakeholderForm"
                isValidateSubmit
            >
                <AddStakeholderForm onSubmit={handleAddStakeholder} />
            </Modal>
        </main>
    );
};

const AddStakeholderForm: React.FC<{ onSubmit: (data: any) => Promise<void> }> = ({ onSubmit }) => {
    const { datalists } = useSelector(getDatalists);
    const statusOptions: Record<string, string> = datalists?.stakeholder_status ?? {};
    return (
        <form
            id="addStakeholderForm"
            onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.currentTarget);
                onSubmit({
                    firstname: String(fd.get('firstname') ?? ''),
                    lastname: String(fd.get('lastname') ?? ''),
                    email: String(fd.get('email') ?? ''),
                    phone: String(fd.get('phone') ?? ''),
                    status: String(fd.get('status') ?? '')
                });
            }}
        >
            <div className="form">
                <div className="form-line">
                    <div className="form-group">
                        <label>Prénom</label>
                        <input name="firstname" type="text" required />
                    </div>
                </div>
                <div className="form-line">
                    <div className="form-group">
                        <label>Nom</label>
                        <input name="lastname" type="text" required />
                    </div>
                </div>
                <div className="form-line">
                    <div className="form-group">
                        <label>Email</label>
                        <input name="email" type="email" />
                    </div>
                </div>
                <div className="form-line">
                    <div className="form-group">
                        <label>Téléphone</label>
                        <input name="phone" type="tel" />
                    </div>
                </div>
                <div className="form-line">
                    <div className="form-group">
                        <label>Statut / lien de parenté</label>
                        <select name="status" defaultValue="">
                            <option value="" disabled>
                                Sélectionnez un lien de parenté
                            </option>
                            {Object.entries(statusOptions).map(([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        </form>
    );
};
